import { http } from './http'
import { RegionSchema, TokenSchema } from '../schemas'
import { z } from 'zod'
import { getLoginUrl, serverVariables } from '../lib'
import { ls } from '../local-storage'
import { parseWithZod } from '../utils/parse-with-zod'

// Clear session on Unauthorized (401) responses
http.onUnauthorized(() => auth.clearSession())

let userRegionHost = ''
let userRegions: z.infer<typeof RegionSchema>[] = []

function hasWebLocks() {
  return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function'
}

/**
 * TODO: remove this once we sync all tabs on login
 * This is used to always fetch the token the first time the app loads,
 * even if the tab is not the leader. This ensures that all tabs have the token
 * when they first load, preventing unnecessary redirects to the login page.
 */
let isGettingTokenFirstTime = true

export const auth = {
  getUserRegionHost() {
    // Prefer server variables over token-based region host
    const serverRegionHost = serverVariables.regionHost
    if (serverRegionHost) return `${location.protocol}//${serverRegionHost}`

    // For subscribers, userRegionHost is set from subscription_region_id
    if (userRegionHost) return `${location.protocol}//${userRegionHost}`

    // For non-subscribers (external/guest users), subscription_region_id is null so
    // userRegionHost is empty. Fall back to matching the deal's region from the URL
    // path against the token's regions list.
    const dealRegionId = serverVariables.regionId
    if (dealRegionId) {
      const region = userRegions.find(r => r.id === dealRegionId)
      if (region) return `${location.protocol}//${region.host}`
    }

    return `${location.protocol}//`
  },

  async getPusherConfig() {
    const regionHost = this.getUserRegionHost()
    const config = await http.get(`${regionHost}/api/sys/config`)
    return parseWithZod(PusherConfigSchema, config)
  },

  async refreshToken(otp?: string) {
    // Use web locks to prevent concurrent refresh token requests across tabs
    const refresh = async () => {
      // If not the tab leader, try to get the token from local storage
      if (!isTabLeader && !otp && !isGettingTokenFirstTime) {
        const currentUserToken = ls.getItem('clofoToken')
        if (currentUserToken) {
          setUserRegionHost(currentUserToken.subscription_region_id, currentUserToken.regions)
          return currentUserToken
        }
      }
      isGettingTokenFirstTime = false
      const result = await http.post('/api/v1/auth/tokens/refresh', otp ? { otp } : undefined)
      const token = parseWithZod(TokenSchema, result)
      ls.setItem('clofoToken', token)
      setUserRegionHost(token.subscription_region_id, token.regions)
      await setTokenInIndexedDB(token)
      return token
    }

    if (!hasWebLocks()) {
      return await refresh()
    }

    return await navigator.locks.request('clofo/refresh-token', refresh)
  },

  async generateAuthUrl({ domain }: { domain: string }) {
    const result = await http.post('/api/v1/saml/generate_auth_url', {
      domain,
    })
    return parseWithZod(GenerateAuthUrlResponseSchema, result).url
  },

  async loginWithPassword({
    email,
    password,
    remember,
  }: {
    email: string
    password: string
    remember: boolean
  }) {
    const result = await http.post('/api/v1/auth/tokens/obtain', {
      email,
      password,
      remember,
    })
    return parseWithZod(TokenSchema, result)
  },

  async resetPassword(email: string) {
    await http.post('/api/v1/auth/forgot_password/request', {
      email,
    })
  },

  async disableTfa(userId: number, payload: { otp: string }) {
    await http.post(`/api/v1/users/${userId}/disable_tfa`, payload)
  },

  async generateTfaSecret(userId: number) {
    const result = await http.post(`/api/v1/users/${userId}/generate_tfa_secret`)
    return parseWithZod(TfaSecretSchema, result)
  },

  async changePassword(payload: {
    email: string
    current_password: string
    new_password1: string
    new_password2: string
  }) {
    return await http.post('/api/v1/auth/change_password', payload)
  },

  async obtainNewToken({
    email,
    password,
    remember,
  }: {
    email: string
    password: string
    remember: boolean
  }) {
    const result = await http.post('/api/v1/auth/tokens/obtain', {
      email,
      password,
      remember,
    })
    return parseWithZod(TokenSchema, result)
  },

  async logout() {
    await http.post('/api/v1/auth/tokens/logout')
    await this.clearSession()
    window.location.href = getLoginUrl()
  },

  async clearSession() {
    const currentAuth = ls.getItem('clofoToken')
    if (currentAuth?.tfa_status === 'sso_verified') {
      await deleteIndexedDBKey('cachedEmail')
    }
    setUserRegionHost(null, [])
    ls.removeItem('clofoToken')
    await deleteIndexedDBKey('token')
    logoutChannel.postMessage({ type: 'logout' })
  },
}

const logoutChannel = new BroadcastChannel('clofo-logout')
logoutChannel.addEventListener('message', event => {
  if (event.data.type === 'logout') {
    http.redirectToLoginWithNextUrl()
  }
})

const GenerateAuthUrlResponseSchema = z.object({
  url: z.string(),
})

const PusherConfigSchema = z.object({
  pusher_app_cluster: z.string(),
  pusher_app_key: z.string(),
})

export type PusherConfig = z.infer<typeof PusherConfigSchema>

const TfaSecretSchema = z.object({
  tfa_secret_uri: z.string(),
})

function setUserRegionHost(
  subscriptionRegionId: number | null,
  regions: z.infer<typeof RegionSchema>[],
) {
  userRegions = regions || []
  userRegionHost = regions?.find(region => region.id === subscriptionRegionId)?.host || ''
}

/**
 * IndexedDB utility functions for storing and deleting auth-related data.
 * This is used to persist the auth token and cached email across sessions and tabs.
 * The token is stored in camelCase format to be consistent with the existing Ember app.
 */
const DATABASE_NAME = 'AuthDatabase'
const TABLE_NAME = 'auth'

function getIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(TABLE_NAME)) {
        db.createObjectStore(TABLE_NAME, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

const setTokenInIndexedDB = async (token: z.infer<typeof TokenSchema>): Promise<void> => {
  try {
    const authdb = await getIndexedDB()
    const transaction = authdb.transaction([TABLE_NAME], 'readwrite')
    const store = transaction.objectStore(TABLE_NAME)

    // Convert token keys to camelCase for consistency with the ember app
    const tokenCamelCase = tokenToCamelCase(token)
    store.put({
      key: 'token',
      value: tokenCamelCase,
    })
    authdb.close()
  } catch (error) {
    console.warn('Failed to store token in IndexedDB:', error)
  }
}

const deleteIndexedDBKey = async (key: 'cachedEmail' | 'token'): Promise<void> => {
  try {
    const authdb = await getIndexedDB()
    const transaction = authdb.transaction([TABLE_NAME], 'readwrite')
    const store = transaction.objectStore(TABLE_NAME)

    store.delete(key)
    authdb.close()
  } catch (error) {
    // Silently handle IndexedDB errors - the cached email clearing is not critical
    console.warn(`Failed to clear key ${key} from IndexedDB:`, error)
  }
}

/**
 * Tab leader election using the Web Locks API.
 * Only the tab that holds the "clofo/tab-leader-election" lock is considered the leader.
 * The leader tab is responsible for refreshing the auth token.
 * The other tabs will get the token from local storage.
 * Once a tab becomes the leader, it will remain the leader until it is closed.
 */
let isTabLeader = false
requestTabLeadership(() => {
  isTabLeader = true
})

export function requestTabLeadership(cb: () => void) {
  if (!hasWebLocks()) {
    cb()
    return () => {}
  }

  let resolve: () => void

  const p = new Promise<void>(res => {
    resolve = res
  })

  navigator.locks.request('clofo/tab-leader-election', () => {
    cb()
    return p
  })

  return () => resolve()
}

function tokenToCamelCase(token: z.infer<typeof TokenSchema>) {
  return {
    email: token.email,
    expAccess: token.exp_access,
    expRefresh: token.exp_refresh,
    iatAccess: token.iat_access,
    iatRefresh: token.iat_refresh,
    organizationId: token.organization_id,
    regions: token.regions,
    subscriptionRegionId: token.subscription_region_id,
    tfaStatus: token.tfa_status,
    userId: token.user_id,
  }
}
